'use client';

import Image from 'next/image';
import type { WhatsappResolved } from './types';
import { toWhatsappLink } from './waLink';
import { withAssetPrefix } from '@/lib/assetPrefix';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';

/**
 * A WhatsApp logo and number that opens a chat (SH-323).
 *
 * The logo renders through `<Image>` from `public/icons/`, **not** through
 * `<Icon>`: that component paints SVGs as a CSS mask over `bg-current` and
 * discards their own colours, which would turn a brand mark into a flat
 * silhouette. Same treatment as the Google logo in the Reviews widget.
 */
export const WhatsappComponent = ({
  resolved,
}: {
  resolved: WhatsappResolved;
}) => {
  const ctx = useOptionalRenderContext();
  const sub = ctx?.substitutePlaceholders ?? ((value: string) => value);

  const phoneNumber = resolved.phoneNumber?.trim();
  if (!phoneNumber) {
    return null;
  }

  const href = toWhatsappLink(phoneNumber);
  if (!href) {
    return null;
  }

  // The editor's own spacing is what makes a number readable, so the displayed
  // text keeps it even though the link does not.
  const label = resolved.label?.trim() ? sub(resolved.label) : phoneNumber;

  return (
    <div className="pt-4">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => pushCmsEventTracking(resolved.tracking)}
        className="inline-flex items-center gap-2 hover:underline"
      >
        <Image
          // A 50×50 WebP with alpha, not an SVG — the brand mark as the
          // customer already serves it. Rendered at 24px, so the extra source
          // pixels cover a 2× display.
          src={withAssetPrefix('/icons/whatsapp.webp')}
          // Named, not decorative. The label defaults to the bare phone
          // number, so without this the link announces as a number with
          // nothing saying where it goes — the mark is the only thing on the
          // row that says WhatsApp. An editor who types the service into the
          // label gets it twice, which is the lesser fault.
          alt="WhatsApp"
          width={24}
          height={24}
          className="shrink-0"
        />
        <span>{label}</span>
      </a>
    </div>
  );
};
